/**
 * Donor pipeline config (CHAP-246) — shared by client + server.
 * 8 stages (Polarity HQ deal model) + 5 capacity tiers + weighted-forecast probs.
 */
export type PipelineStage =
  | 'discovered' | 'qualified' | 'contacted' | 'pitched'
  | 'negotiating' | 'signed' | 'declined' | 'lost_contact';

export type CapacityTier = 'major' | 'leadership' | 'mid' | 'broad' | 'unknown';

export interface PipelineStageMeta {
  key: PipelineStage;
  label: string;
  closeProbability: number; // weighted-forecast factor
  terminal: boolean;
  /** CHAP-246 — ping the owner after this many days of no movement (null = no reminder). */
  touchCadenceDays: number | null;
}

export const PIPELINE_STAGES: readonly PipelineStageMeta[] = [
  { key: 'discovered', label: 'Identification', closeProbability: 0, terminal: false, touchCadenceDays: null },
  { key: 'qualified', label: 'Qualification', closeProbability: 0, terminal: false, touchCadenceDays: 14 },
  { key: 'contacted', label: 'Cultivation', closeProbability: 0.1, terminal: false, touchCadenceDays: 7 },
  { key: 'pitched', label: 'Solicitation', closeProbability: 0.3, terminal: false, touchCadenceDays: 14 },
  { key: 'negotiating', label: 'Negotiation', closeProbability: 0.6, terminal: false, touchCadenceDays: 7 },
  { key: 'signed', label: 'Pledge', closeProbability: 1, terminal: true, touchCadenceDays: null },
  { key: 'declined', label: 'Closed lost', closeProbability: 0, terminal: true, touchCadenceDays: null },
  { key: 'lost_contact', label: 'Lost contact', closeProbability: 0, terminal: true, touchCadenceDays: null },
];

export const STAGE_CADENCE_DAYS: Record<PipelineStage, number | null> = PIPELINE_STAGES.reduce(
  (acc, s) => ({ ...acc, [s.key]: s.touchCadenceDays }),
  {} as Record<PipelineStage, number | null>,
);

/** Whole days from an ISO timestamp (or null) to `now`. null → Infinity (never touched). */
export function daysSince(iso: string | null, now: Date): number {
  if (!iso) return Infinity;
  return Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
}

/** A record is due for follow-up if its stage has a cadence and it has gone stale. */
export function isDueForFollowup(stage: PipelineStage, lastTouchIso: string | null, now: Date): boolean {
  const cadence = STAGE_CADENCE_DAYS[stage];
  if (cadence == null) return false;
  return daysSince(lastTouchIso, now) >= cadence;
}

export interface CapacityTierMeta {
  key: CapacityTier;
  label: string;
  color: string;
}

export const CAPACITY_TIERS: readonly CapacityTierMeta[] = [
  { key: 'major', label: 'Major ($250K+)', color: '#7c3aed' },
  { key: 'leadership', label: 'Leadership ($50K-250K)', color: '#2563eb' },
  { key: 'mid', label: 'Mid ($5K-50K)', color: '#0d9488' },
  { key: 'broad', label: 'Broad (<$5K)', color: '#65a30d' },
  { key: 'unknown', label: 'Unknown', color: '#9ca3af' },
];

export const STAGE_PROBABILITY: Record<PipelineStage, number> = PIPELINE_STAGES.reduce(
  (acc, s) => ({ ...acc, [s.key]: s.closeProbability }),
  {} as Record<PipelineStage, number>,
);

/** Weighted forecast: sum of close-probability × pledged amount across records. */
export function weightedForecast(records: Array<{ pipelineStage: PipelineStage; pipelinePledgedAmount: number }>): number {
  return Math.round(records.reduce((s, r) => s + STAGE_PROBABILITY[r.pipelineStage] * (r.pipelinePledgedAmount || 0), 0));
}

export interface PipelineRecord {
  id: string;
  name: string;
  email: string | null;
  pipelineStage: PipelineStage;
  capacityTier: CapacityTier;
  ownerName: string | null;
  pipelinePledgedAmount: number;
  pipelineCommittedAmount: number;
  pipelineLastTouchAt: string | null;
  // CHAP-254 — address health
  addressHealth: 'valid' | 'bounced_recently' | 'marked_invalid' | 'unknown';
  addressLastGoodAt: string | null;
}

export interface FollowUpItem {
  id: string;
  name: string;
  pipelineStage: PipelineStage;
  ownerName: string | null;
  daysSinceTouch: number | null;
  cadenceDays: number | null;
}

export interface PipelineResponse {
  stages: PipelineStageMeta[];
  tiers: CapacityTierMeta[];
  stageProbability: Record<PipelineStage, number>;
  byStage: Record<string, { count: number; pledged: number }>;
  forecastCents: number;
  followUpQueue: FollowUpItem[];
  total: number;
  records: PipelineRecord[];
}
